import React, {
  useMemo,
  useState,
} from 'react';
import clsx from 'clsx';
import Table from 'core/components/VirtualTable';
import {
  VirtualTableColumnDef as ColumnDef,
} from 'core/components/VirtualTable/types';
import {
  PopulateSupportUserDTO,
} from 'dtos';
import Input from 'pages/Dashboard/components/Input';
import SearchIcon from 'core/icons/search';
import Button from 'pages/Dashboard/components/Button';
import {
  populateSupportEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import Tooltip from 'core/components/Tooltip';
import Tag from 'core/components/Tag';

type Props = {
  users: PopulateSupportUserDTO[];
  isLoading: boolean;
};

const columns: ColumnDef<PopulateSupportUserDTO>[] = [{
  id: 'fullName',
  columnTitle: 'Name',
  accessorKey: 'fullName',
  meta: { autoSized: true },
  className: '!font-medium',
  cell: ({ row }) => (
    <div className="flex items-center gap-2 min-w-0">
      <span className="truncate">{row.original.fullName}</span>
      {!row.original.isActive && (
        <Tag
          tagName="Inactive"
          className="!bg-gray-200 !text-gray-700"
        />
      )}
    </div>
  ),
}, {
  id: 'title',
  columnTitle: 'Title',
  accessorKey: 'title',
  size: 180,
}, {
  id: 'designation',
  columnTitle: 'Designation',
  size: 180,
  cell: ({ row }) => row.original.designation ?? '--',
}, {
  id: 'email',
  columnTitle: 'Email',
  accessorKey: 'email',
  size: 250,
  cell: ({ row }) => row.original.email ?? '--',
}, {
  id: 'actions',
  accessorKey: 'actions',
  header: '',
  size: 120,
  cell: ({ row }) => (
    <Tooltip
      title={row.original.isActive
        ? `This will log you in as ${row.original.fullName}.`
        : 'You cannot impersonate an inactive user.'}
    >
      <span className="inline-flex">
        <Button
          variant="text"
          color="primary"
          disabled={!row.original.isActive}
          className={clsx(
            '!px-2 !py-1 !rounded-md',
            row.original.isActive
              ? '!text-amber-600 hover:!bg-amber-100'
              : '!text-slate-400 !cursor-not-allowed',
          )}
          onClick={(e) => {
            e.stopPropagation();
            if (!row.original.isActive) return;
            populateSupportEventChannel.emit('onImpersonateUser', row.original);
          }}
        >
          Impersonate
        </Button>
      </span>
    </Tooltip>
  ),
}];

export default function UserList({
  users,
  isLoading,
}: Props) {
  const [searchQuery, setSearchQuery] = useState('');
  const [inactiveOnly, setInactiveOnly] = useState(false);

  const inactiveCount = useMemo(
    () => users.filter((u) => !u.isActive).length,
    [users],
  );

  const filteredUsers = useMemo(() => {
    let result = users;
    if (inactiveOnly) {
      result = result.filter((user) => !user.isActive);
    }

    if (!searchQuery.trim()) return result;

    const query = searchQuery.toLowerCase();
    return result.filter((user) => user.fullName.toLowerCase().includes(query)
      || user.title.toLowerCase().includes(query)
      || user.designation?.toLowerCase().includes(query)
      || user.email?.toLowerCase().includes(query));
  }, [users, searchQuery, inactiveOnly]);

  return (
    <div className="flex flex-col gap-4 h-full py-6 overflow-hidden">
      <div className="flex justify-between px-6 gap-4">
        <div className="flex flex-col gap-3">
          <h1 className="text-base font-semibold">Users</h1>
          <div className="flex items-center gap-2">
            {!isLoading && (
              <Tag
                tagName={`All (${users.length})`}
                onClick={() => setInactiveOnly(false)}
                className={clsx(
                  'cursor-pointer select-none',
                  !inactiveOnly
                    ? '!bg-slate-900 !text-white'
                    : '!bg-gray-200 !text-gray-700 hover:!bg-gray-300',
                )}
                tooltip={!inactiveOnly ? 'Showing all users' : 'Show all users'}
              />
            )}
            {inactiveCount > 0 && (
              <Tag
                tagName={`Inactive (${inactiveCount})`}
                onClick={() => setInactiveOnly(true)}
                className={clsx(
                  'cursor-pointer select-none',
                  inactiveOnly
                    ? '!bg-slate-900 !text-white'
                    : '!bg-gray-200 !text-gray-700 hover:!bg-gray-300',
                )}
                tooltip={inactiveOnly ? 'Showing inactive users only' : 'Filter to inactive users'}
              />
            )}
          </div>
        </div>
        <Input
          placeholder="Search users by name, title, designation, or email..."
          className="w-96"
          value={searchQuery}
          onChange={(val: string) => {
            setSearchQuery(val);
          }}
          inputProps={{ autoComplete: 'off' }}
          icon={<SearchIcon className="!w-4 !h-4 text-gray-400" />}
          id="patient-search"
        />
      </div>
      <div className="relative flex flex-col flex-1 min-h-[400px] overflow-y-auto">
        {isLoading ? (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-slate-500 text-sm">Loading users...</div>
          </div>
        ) : (
          <Table<PopulateSupportUserDTO>
            data={filteredUsers}
            getRowId={(row) => row.userId}
            columns={columns}
            emptySlot="No users found"
            classes={{
              header: 'px-4',
              scrollContainer: 'justify-center items-center !pb-0',
              outerRow: (row) => clsx(
                '!w-[calc(100%-32px)] left-4 hover:border-[#dde2ee]',
                { 'opacity-70': !row.original.isActive },
              ),
              row: () => clsx(
                '!items-center py-2 cursor-pointer hover:rounded-md',
              ),
            }}
          />
        )}
      </div>
    </div>
  );
}
